import client from "../network/client"
import { STORES, idbClear, idbDelete, idbGetAll, idbPut } from "./db"

// A request that keeps failing with a 5xx is dropped after this many tries.
const MAX_SERVER_ATTEMPTS = 5

export const OUTBOX_KIND = { ACTIVITY: "activity", FLAG: "flag" }

const SEND = { SENT: "sent", DROP: "drop", RETRY: "retry", OFFLINE: "offline" }

// Holds entries only when IndexedDB is unavailable.
const memoryQueue = []
const listeners = new Set()
let flushing = null
let flushRequested = false
let memorySequence = 0

export async function enqueue({ kind, restaurantId, request }) {
  const entry = { kind, restaurantId, request, createdAt: Date.now(), attempts: 0 }
  const id = await idbPut(STORES.OUTBOX, entry)
  if (id === null) {
    memorySequence += 1
    memoryQueue.push({ ...entry, id: `memory-${memorySequence}` })
  }
  notify()
}

async function listEntries() {
  const stored = await idbGetAll(STORES.OUTBOX)
  return [...stored, ...memoryQueue].sort((a, b) => a.createdAt - b.createdAt)
}

export async function pendingCounts() {
  const entries = await listEntries()
  return {
    total: entries.length,
    flags: entries.filter((entry) => entry.kind === OUTBOX_KIND.FLAG).length
  }
}

export function subscribeOutbox(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function notify() {
  listeners.forEach((listener) => listener())
}

// Sends queued requests oldest first and stops at the first sign of being offline.
export function flushOutbox() {
  if (flushing) {
    // Entries queued during a flush get another pass once it finishes.
    flushRequested = true
    return flushing
  }
  flushing = (async () => {
    const attempted = new Set()
    do {
      flushRequested = false
      for (const entry of await listEntries()) {
        if (attempted.has(entry.id)) continue
        attempted.add(entry.id)
        const outcome = await send(entry)
        if (outcome === SEND.OFFLINE) return
        if (outcome === SEND.RETRY) await saveEntry({ ...entry, attempts: entry.attempts + 1 })
        else await removeEntry(entry)
      }
    } while (flushRequested)
  })()
    .catch(() => {})
    .finally(() => {
      flushRequested = false
      flushing = null
      notify()
    })
  return flushing
}

async function send(entry) {
  try {
    await client.request({
      ...entry.request,
      headers: entry.restaurantId ? { "restaurant-id": entry.restaurantId } : undefined
    })
    return SEND.SENT
  } catch (error) {
    const status = error?.status ?? null
    // No response, or a session being torn down: keep it for the next flush.
    if (status === null || status === 401) return SEND.OFFLINE
    if (status < 500) return SEND.DROP
    return entry.attempts + 1 >= MAX_SERVER_ATTEMPTS ? SEND.DROP : SEND.RETRY
  }
}

async function saveEntry(entry) {
  const index = memoryQueue.findIndex((queued) => queued.id === entry.id)
  if (index >= 0) memoryQueue[index] = entry
  else await idbPut(STORES.OUTBOX, entry)
}

async function removeEntry(entry) {
  const index = memoryQueue.findIndex((queued) => queued.id === entry.id)
  if (index >= 0) memoryQueue.splice(index, 1)
  else await idbDelete(STORES.OUTBOX, entry.id)
}

export async function clearOutbox() {
  memoryQueue.length = 0
  await idbClear(STORES.OUTBOX)
  notify()
}
